import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { signup } from '../services/api'
import { errorText } from '../utils/errorText'
import { HIDDEN_INPUT_NAMES, rememberTrialQuery, trialTrackingFields, type TrialTrackingFields } from '../utils/trialTracking'
import { trialRegion } from '../utils/trialRegion'
import { resolveCountryCode } from '../utils/countryCode'
import {
  MARKETING_CONSENT_TEXT,
  PRIVACY_POLICY_URL,
  TERMS_ERROR_TEXT,
  TERMS_OF_SERVICE_URL,
  marketingConsentIsImplicit,
} from '../utils/marketingConsent'
import {
  ENDPOINTS_TO_MANAGE_ERROR_TEXT,
  ENDPOINTS_TO_MANAGE_LABEL,
  ENDPOINTS_TO_MANAGE_OPTIONS,
  isEndpointsToManage,
} from '../utils/endpointsToManage'
import { sendRudderStackTrackEvent } from '../utils/marketingAnalytics'
import { useMarketingTrackers } from '../hooks/useMarketingTrackers'
import { useHubSpotTrackingCookie } from '../hooks/useHubSpotTrackingCookie'

const FIELD_CLASS =
  'mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none'
const CHECKBOX_CLASS =
  'mt-0.5 h-4 w-4 shrink-0 rounded border-theme-border-primary bg-theme-input text-blue-600 focus:ring-blue-500'
// How long "Send again" waits after a link has been sent; the backend sends at most one mail
// per address in that time.
const RESEND_WAIT_SECONDS = 60

/** The tracking inputs, or none: a failure reading them must not cost the visitor the sign-up. */
function trackingFields(location: { pathname: string; search: string }): TrialTrackingFields {
  try {
    return trialTrackingFields(location)
  } catch {
    return {}
  }
}

export default function SignUp() {
  const location = useLocation()
  const { search } = location
  const [form, setForm] = useState({ full_name: '', company_name: '', email: '' })
  // The dropdown's value: '' until the visitor picks a range (there is no default).
  const [endpointsToManage, setEndpointsToManage] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [marketingOptIn, setMarketingOptIn] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  // The address an activation link was sent to; the form is shown again while it is null.
  const [sentTo, setSentTo] = useState<string | null>(null)
  // Seconds until "Send again" is offered.
  const [resendWait, setResendWait] = useState(0)
  // The address whose sign-up event has gone out: once per address, however often its link
  // is sent; a sign-up for another address after "Edit address" sends its own.
  const eventSentFor = useRef<string | null>(null)
  const [countryCode, setCountryCode] = useState<string | undefined>(undefined)
  // The lookup in flight, so a submit that comes before its answer can wait for it.
  const countryLookup = useRef<Promise<string | undefined>>(Promise.resolve(undefined))

  useEffect(() => rememberTrialQuery(search), [search])
  useMarketingTrackers()
  // Re-renders once the HubSpot tracker has set its cookie, so the hidden hutk input shows it.
  useHubSpotTrackingCookie()

  const counting = resendWait > 0
  useEffect(() => {
    if (!counting) return
    const timer = setInterval(() => setResendWait((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(timer)
  }, [counting])

  // Resolved while the visitor types.
  useEffect(() => {
    let cancelled = false
    const lookup = resolveCountryCode(search)
    countryLookup.current = lookup
    lookup.then((code) => {
      if (!cancelled) setCountryCode(code)
    })
    return () => {
      cancelled = true
    }
  }, [search])

  // Implicit consent outside marketing's explicit-opt-in countries, and while the country is
  // unknown: the statement without a checkbox, sent as given.
  const implicitConsent = marketingConsentIsImplicit(countryCode)
  const marketingConsent = implicitConsent || marketingOptIn
  const region = trialRegion(countryCode)

  // The HubSpot form's hidden inputs. Read on every render so the inspector shows the values
  // the submit will send, the tracking cookie included once the HubSpot script has set it.
  // `marketing-optin` is the website's own name for the consent state, shown as the boolean
  // marketing's console check expects, while the request carries marketing_consent_check.
  const hiddenInputs: Record<string, string | undefined> = {
    ...trackingFields(location),
    region,
    'marketing-optin': marketingConsent ? 'true' : 'false',
  }

  // Sends the sign-up request; the backend mails the activation link. Also what "Send again"
  // does, with the fields as they were sent.
  const send = async () => {
    setError(null)
    if (!isEndpointsToManage(endpointsToManage)) {
      setError(ENDPOINTS_TO_MANAGE_ERROR_TEXT)
      return
    }
    setIsLoading(true)
    try {
      // A lookup still in flight is waited for (it has its own timeout), so the sign-up
      // carries the country whenever one can be had. The consent follows that country:
      // implicit outside the explicit-opt-in list, else the checkbox as the visitor left it
      // (unticked when it was never shown).
      const code = countryCode ?? (await countryLookup.current.catch(() => undefined))
      const consent = marketingConsentIsImplicit(code) || marketingOptIn
      const { spid_trial_src_ref__c: sourceRef, ...hidden } = trackingFields(location)
      const response = await signup({
        ...form,
        endpoints_to_manage: endpointsToManage,
        ...(sourceRef ? { trial_source_ref: sourceRef } : {}),
        ...(code ? { country_code: code } : {}),
        hubspot_form: { ...hidden, region: trialRegion(code), marketing_consent_check: consent },
      })
      setSentTo(form.email)
      setResendWait(RESEND_WAIT_SECONDS)
      if (eventSentFor.current !== form.email) {
        eventSentFor.current = form.email
        try {
          // The sign-up event goes out when the form is submitted, on the device whose
          // marketing identity it carries: the activation click may come from another one.
          // Which plan the account will land on decides the event: a free-plan sign-up is its
          // own funnel step, and the trial event goes out from the backend when the tenant
          // later starts the trial. A sign-up that starts a trial (the legacy model) keeps
          // the pair marketing has always received.
          if (response.plan === 'trial') {
            sendRudderStackTrackEvent('Sign_Up_SAP')
            sendRudderStackTrackEvent('Trial_Any')
          } else {
            sendRudderStackTrackEvent('Sign_Up_Free_SAP')
          }
        } catch {
          // The link is on its way; analytics that fail are their own loss.
        }
      }
    } catch (err: any) {
      setError(errorText(err, 'Sign up failed. Please try again.'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    // Checked here rather than with the browser's own validation, so the error is marketing's
    // text and shows where the other form errors do.
    if (!isEndpointsToManage(endpointsToManage)) {
      setError(ENDPOINTS_TO_MANAGE_ERROR_TEXT)
      return
    }
    if (!termsAccepted) {
      setError(TERMS_ERROR_TEXT)
      return
    }
    await send()
  }

  const field = (label: string, key: keyof typeof form, type = 'text', placeholder = '', autoComplete?: string) => (
    <div>
      <label className="block text-sm font-medium text-theme-text-secondary mb-1">{label}</label>
      <input
        type={type}
        required
        autoComplete={autoComplete}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className={FIELD_CLASS}
        placeholder={placeholder}
      />
    </div>
  )

  // No option is selected until the visitor picks one: the first option has no value.
  const endpointsDropdown = (
    <div>
      <label htmlFor="endpoints_to_manage" className="block text-sm font-medium text-theme-text-secondary mb-1">
        {ENDPOINTS_TO_MANAGE_LABEL}
      </label>
      <select
        id="endpoints_to_manage"
        name="endpoints_to_manage"
        value={endpointsToManage}
        onChange={(e) => setEndpointsToManage(e.target.value)}
        aria-invalid={error === ENDPOINTS_TO_MANAGE_ERROR_TEXT || undefined}
        className={`${FIELD_CLASS} ${endpointsToManage ? '' : 'text-theme-text-secondary'}`}
      >
        <option value="">Select a range</option>
        {ENDPOINTS_TO_MANAGE_OPTIONS.map((range) => (
          <option key={range} value={range}>
            {range}
          </option>
        ))}
      </select>
    </div>
  )

  const marketingCheckbox = (
    <label className="flex items-start gap-2 text-sm text-theme-text-secondary">
      <input
        type="checkbox"
        name="marketing_consent_check"
        checked={marketingOptIn}
        onChange={(e) => setMarketingOptIn(e.target.checked)}
        className={CHECKBOX_CLASS}
      />
      <span>{MARKETING_CONSENT_TEXT}</span>
    </label>
  )

  if (sentTo !== null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary py-10">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary text-center" data-testid="activation-sent">
          <img src="/splashtop-mark.png" alt="Splashtop" className="w-10 h-10 object-contain mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-theme-text-primary">Check your inbox</h2>
          <p className="mt-3 text-sm text-theme-text-secondary">
            We sent an activation link to <span className="font-medium text-theme-text-primary">{sentTo}</span>.
            It is valid for 24 hours.
          </p>
          <p className="mt-2 text-sm text-theme-text-secondary">Open it to set your password and finish creating your account.</p>

          {error && (
            <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md" role="alert">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          <button
            type="button"
            onClick={send}
            disabled={isLoading || resendWait > 0}
            className="mt-6 w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Sending...' : resendWait > 0 ? `Send again (${resendWait}s)` : 'Send again'}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null)
              setSentTo(null)
            }}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Edit address
          </button>

          <div className="mt-4 text-center text-sm text-theme-text-secondary">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-500 hover:text-blue-400">Sign in</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-secondary py-10">
      <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
        <img src="/splashtop-logo.png" alt="Splashtop" className="h-9 w-auto mx-auto mb-4 dark:brightness-0 dark:invert" />
        <h2 className="text-2xl font-bold text-center text-theme-text-primary mb-2">Get started free</h2>
        <p className="text-center text-sm text-theme-text-secondary mb-6">Full features. No credit card required.</p>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md" role="alert">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {field('Full name', 'full_name', 'text', 'Jane Doe', 'name')}
          {field('Company', 'company_name', 'text', 'Acme Corp', 'organization')}
          {field('Work email', 'email', 'email', 'jane@acme.com', 'email')}
          {endpointsDropdown}

          {/* The HubSpot form's hidden inputs, filled from the sign-up link and the page. Every
              one is present, empty or not, so marketing's script finds each of them. */}
          {HIDDEN_INPUT_NAMES.map((name) => (
            <input key={name} type="hidden" name={name} value={hiddenInputs[name] ?? ''} readOnly />
          ))}

          <div className="space-y-3 pt-1">
            {!implicitConsent && marketingCheckbox}

            <label className="flex items-start gap-2 text-sm text-theme-text-secondary">
              <input
                type="checkbox"
                name="terms_accepted"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                aria-invalid={error === TERMS_ERROR_TEXT || undefined}
                className={CHECKBOX_CLASS}
              />
              <span>
                I agree to the Splashtop{' '}
                <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400">
                  Terms of Service
                </a>
                . We process your information in accordance with our{' '}
                <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400">
                  Privacy Policy
                </a>
              </span>
            </label>

            {implicitConsent && <p className="text-sm text-theme-text-secondary">{MARKETING_CONSENT_TEXT}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Sending your activation link...' : 'Create account'}
          </button>
        </form>

        <div className="mt-4 text-center text-sm text-theme-text-secondary">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-500 hover:text-blue-400">Sign in</Link>
        </div>
      </div>
    </div>
  )
}
